
import { Options, Vue } from 'vue-class-component'
import { Emit, Prop } from 'vue-property-decorator'
import BarcodeInfoLabel from '../components/BarcodeInfoLabel.vue'
import KeyenceHTHardKeyButton from '@/components/keyenceHT/hardkeys/KeyenceHTHardKeyButton.vue'
import IconVerticalThreeDots from '../components/svg/IconVerticalThreeDots.vue'
import { toSnakeCase, deepSearch, objectToArray, parseCustomFieldsValue } from '../utils/helpers'
import { MASKED_STR, LINKED_BARCODE_HREF_TYPE, LINKED_BARCODE_PATH_KEY } from '../utils/constants'
import isEmpty from 'lodash/isEmpty'
import { IActivationData, IBarcode } from '@/utils/types/barcodeTypes'
import { IBarcodeDefinitionType, IFormInputSchema } from '@/utils/types/fieldDefinition'
import { IDimension, ILabelDisplay, IProduct, IRedirectData } from '@/utils/types/commonTypes'
import { KeyenceHardKey } from '@/utils/keyenceHT/keyenceHardKeyEventHandler'
import { isKeyenceEnterpriseBrowser } from '@/utils/keyenceHT/keyenceModules'

@Options({
  components: { BarcodeInfoLabel, IconVerticalThreeDots, KeyenceHTHardKeyButton },
  emits: ['barcode:unlink', 'barcode:redirect'],
})
export default class LinkingBarcodeInfo extends Vue {
  KeyenceHardKey = KeyenceHardKey

  get isKeyenceEnterpriseBrowser() {
    return isKeyenceEnterpriseBrowser()
  }

  @Prop({ type: String }) readonly mainBarcodeId!: string
  @Prop({ type: String }) readonly trackingNumberMes!: string
  @Prop({ type: Object }) readonly barcode!: IBarcode
  @Prop({ type: Object }) readonly barcodeTypeInfo!: IBarcodeDefinitionType
  @Prop({ type: Object }) readonly product?: IProduct
  @Prop({ type: String }) readonly mainKey!: string
  @Prop({ type: Number }) readonly indexMainKey?: number
  @Prop({ type: Number }) readonly indexLinkedBarcode!: number
  @Prop({ default: false }) readonly displayMenu?: boolean
  @Prop({ type: String }) readonly linkedBarcodeHrefType!: string

  dialogVisible = false
  customPackageInfo = {} as Record<string, ILabelDisplay>

  get barcodeTypeName() {
    return this.barcodeTypeInfo?.name
  }

  get trackingNumberLabel() {
    const tpLabel = this.barcodeTypeInfo?.activationFields?.trackingNumber?.label
    return !isEmpty(tpLabel) ? tpLabel : this.$t('tracking_number')
  }

  get dimension() {
    this.setCustomFieldsValue()
    const rawDimensionData = {
      ...this.barcode?.activationData?.dimension,
    } as IDimension
    const localeNumber = {
      width: rawDimensionData?.width?.toLocaleString(),
      height: rawDimensionData?.height?.toLocaleString(),
      weight: rawDimensionData?.weight?.toLocaleString(),
      depth: rawDimensionData?.depth?.toLocaleString(),
    }

    const dimension = Object.entries(localeNumber).reduce(
      (acc, [key, value]) => ({
        ...acc,
        [key]: {
          label: key,
          value: value,
        },
      }),
      {}
    )

    const packageInfo = {
      // ...Product
      ...(this.product && { productName: this.product.name }),

      // ...Package info (dimension)
      ...(this.isDimensionAvailable && this.barcode?.activationData?.dimension && dimension),
      // ...Custom fields
      ...this.customPackageInfo,
    }

    return { ...packageInfo }
  }

  get externalIdLabel(): string {
    return this.$t('externalId')
  }

  get maskedCustomField() {
    if (!this.barcodeTypeInfo) return
    const ignoredKey = ['imagePaths', 'imageUrls', 'filePaths', 'productId', 'externalId']

    const maskedCustomField = {} as Record<string, string | string[]>
    const maskedFields =
      this.barcode?.activationData?.maskedFields?.filter((val: string) => {
        if (val !== 'origin' && val !== 'destination') {
          return val
        }
      }) || []
    maskedFields.forEach((key: string) => {
      let result: IFormInputSchema = {}
      deepSearch(
        this.barcodeTypeInfo?.activationFields,
        key.split('.').length > 0 ? key.split('.')[1] : key,
        (k: string, v: IFormInputSchema) => (result = v)
      )

      if (result.isHidden) {
        return
      }

      if (result?.label) {
        maskedCustomField[result?.label] = MASKED_STR
      } else {
        const arr = objectToArray<Record<string, string>>(result as Record<string, Record<string, string>>) || []
        arr.forEach((el) => {
          if (el?.value?.label) {
            maskedCustomField[el?.value?.label] = MASKED_STR
          }
        })
      }

      if (key === 'dimension') {
        delete maskedCustomField[key]
        maskedCustomField.width = MASKED_STR
        maskedCustomField.height = MASKED_STR
        maskedCustomField.depth = MASKED_STR
        maskedCustomField.weight = MASKED_STR
      }
      if (ignoredKey.includes(key)) {
        delete maskedCustomField[key]
      }
    })
    return maskedCustomField
  }

  closeUnlinkConfirmDialog() {
    this.dialogVisible = false
  }

  @Emit('barcode:unlink')
  onUnlink() {
    this.dialogVisible = false
    return this.mainBarcodeId
  }

  @Emit('barcode:redirect')
  moveTo(): IRedirectData {
    return this.linkedBarcodeHrefType && this.linkedBarcodeHrefType !== LINKED_BARCODE_HREF_TYPE.snapshot
      ? { mainBarcodeId: this.barcode.id }
      : {
          mainBarcodeId: this.mainBarcodeId,
          path:
            this.mainKey === LINKED_BARCODE_PATH_KEY.activationData
              ? `${this.mainKey}.${LINKED_BARCODE_PATH_KEY.linkedBarcodes}.${this.indexLinkedBarcode}`
              : `${this.mainKey}.${this.indexMainKey}.${LINKED_BARCODE_PATH_KEY.linkedBarcodes}.${this.indexLinkedBarcode}`,
        }
  }

  toSnakeCase(s: string) {
    return toSnakeCase(s)
  }

  getDisplayValue(value: string | number) {
    return typeof value === 'number' ? value.toFixed(1) : value
  }

  flattenFields(obj: IActivationData): Record<string, string | string[]> {
    const fields: { [key: string]: unknown } = { ...obj }
    return Object.keys(fields).reduce(
      (acc, cur) => (cur.match(/^custom[a-zA-Z]/g) ? { ...acc, ...(fields[cur] as object) } : { ...acc }),
      {}
    )
  }

  get isDimensionAvailable() {
    return this.barcodeTypeInfo?.activationFields?.dimension?.isAvailable
  }

  setCustomFieldsValue() {
    if (!this.barcodeTypeInfo) return
    const customFieldsDefinition = this.barcodeTypeInfo?.activationFields?.customFields || {}
    parseCustomFieldsValue(this.customPackageInfo, customFieldsDefinition, this.barcode)
  }
}
